import { AxiosError } from 'axios';

/**
 * Structured error details extracted from API errors.
 * Provides meaningful information instead of minified variable names.
 */
export interface ApiErrorDetails {
  message: string;
  status?: number;
  statusText?: string;
  data?: unknown;
  url?: string;
  code?: string;
}

/**
 * Extracts meaningful error information from various error types.
 * Handles Axios errors, standard Errors, and unknown types.
 *
 * @param error - The caught error of any type
 * @returns Structured error details
 */
export const extractApiError = (error: unknown): ApiErrorDetails => {
  // Handle Axios errors with full response details
  if (error instanceof AxiosError) {
    return {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      code: error.code,
    };
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return { message: error.message };
  }

  // Handle unknown error types
  return { message: String(error) };
};

/**
 * Logs API errors with full structured details.
 * Prevents minified variable names from appearing in production logs.
 *
 * @param context - A descriptive context string (e.g., function name)
 * @param error - The caught error of any type
 */
export const logApiError = (context: string, error: unknown): void => {
  const details = extractApiError(error);

  console.error(`[${context}]`, {
    message: details.message,
    status: details.status,
    statusText: details.statusText,
    url: details.url,
    code: details.code,
    data: details.data,
  });
};

/**
 * Creates a user-friendly error message from API error details.
 *
 * @param error - The caught error of any type
 * @param defaultMessage - Fallback message if no specific message can be extracted
 * @returns A user-friendly error message string
 */
export const getUserFriendlyErrorMessage = (
  error: unknown,
  defaultMessage: string = 'An unexpected error occurred. Please try again.'
): string => {
  const details = extractApiError(error);

  // Handle specific HTTP status codes
  if (details.status === 401) {
    return 'Your session has expired. Please log in again.';
  }
  if (details.status === 403) {
    return 'You do not have permission to perform this action.';
  }
  if (details.status === 404) {
    return 'The requested resource was not found.';
  }
  if (details.status === 429) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  if (details.status && details.status >= 500) {
    return 'A server error occurred. Please try again later.';
  }

  // Handle network errors
  if (details.code === 'ERR_NETWORK') {
    return 'Network error. Please check your internet connection.';
  }
  if (details.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.';
  }

  // Check for server-provided error message
  if (details.data && typeof details.data === 'object' && 'message' in details.data) {
    return String((details.data as { message: unknown }).message);
  }

  return defaultMessage;
};
